import React, { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

interface ProgressBarProps {
  value: number;
  maxValue: number;
  label?: string;
  showPercentage?: boolean;
  height?: string;
  colorClass?: string;
  animate?: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  maxValue,
  label,
  showPercentage = true,
  height = "h-4",
  colorClass = "bg-blue-500",
  animate = true
}) => {
  const prefersReducedMotion = useReducedMotion();
  const [hasAnimated, setHasAnimated] = useState(false);
  const percentage = Math.min(Math.round((value / maxValue) * 100), 100);

  // Track first animation
  useEffect(() => {
    if (animate && !hasAnimated) {
      const timer = setTimeout(() => setHasAnimated(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [animate, hasAnimated]);

  // Replace standard color class with catppuccin equivalent if in dark mode
  const darkModeColorClass = colorClass
    .replace('bg-blue-500', 'dark:bg-catppuccin-blue')
    .replace('bg-red-500', 'dark:bg-catppuccin-red')
    .replace('bg-green-500', 'dark:bg-catppuccin-green')
    .replace('bg-yellow-500', 'dark:bg-catppuccin-yellow')
    .replace('bg-purple-500', 'dark:bg-catppuccin-mauve')
    .replace('bg-pink-500', 'dark:bg-catppuccin-pink')
    .replace('bg-indigo-500', 'dark:bg-catppuccin-lavender')
    .replace('bg-orange-500', 'dark:bg-catppuccin-peach');

  const finalColorClass = `${colorClass} ${darkModeColorClass}`;

  // Shimmer animation effect
  const shimmerVariants = {
    animate: {
      x: ['-100%', '100%'],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'linear' as const,
        repeatDelay: 3
      }
    }
  };

  // If reduced motion is preferred, render without animations
  if (prefersReducedMotion || !animate) {
    return (
      <div className="w-full">
        {label && (
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-gray-700 dark:text-catppuccin-text dark:font-medium">{label}</span>
            {showPercentage && (
              <span className="text-sm font-medium text-gray-500 dark:text-catppuccin-lavender">{percentage}%</span>
            )}
          </div>
        )}
        <div className={`w-full ${height} bg-gray-200 dark:bg-catppuccin-surface1 rounded-full overflow-hidden`}>
          <div
            className={`${finalColorClass} rounded-full ${height}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {label && (
        <motion.div
          className="flex justify-between mb-1"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <span className="text-sm font-medium text-gray-700 dark:text-catppuccin-text dark:font-medium">{label}</span>
          {showPercentage && (
            <motion.span
              className="text-sm font-medium text-gray-500 dark:text-catppuccin-lavender"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {percentage}%
            </motion.span>
          )}
        </motion.div>
      )}
      <div className={`w-full ${height} bg-gray-200 dark:bg-catppuccin-surface1 rounded-full overflow-hidden relative`}>
        <motion.div
          className={`${finalColorClass} rounded-full ${height} relative overflow-hidden`}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{
            duration: hasAnimated ? 0.5 : 1.2,
            ease: [0.25, 0.1, 0.25, 1],
            delay: hasAnimated ? 0 : 0.2
          }}
        >
          {/* Shimmer effect */}
          <motion.div
            className="absolute inset-0 w-full"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              width: '50%'
            }}
            variants={shimmerVariants}
            animate="animate"
          />

          {/* Subtle pulse on the fill */}
          <motion.div
            className={`absolute inset-0 ${finalColorClass} opacity-50`}
            animate={{
              opacity: [0.5, 0.7, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />
        </motion.div>

        {/* Progress marker glow at the end */}
        {percentage > 0 && percentage < 100 && (
          <motion.div
            className="absolute top-0 h-full w-1"
            style={{ left: `${percentage}%`, transform: 'translateX(-50%)' }}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.5, 1, 0.5],
              boxShadow: [
                '0 0 4px 2px rgba(59, 130, 246, 0.3)',
                '0 0 8px 4px rgba(59, 130, 246, 0.5)',
                '0 0 4px 2px rgba(59, 130, 246, 0.3)'
              ]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ProgressBar;
